<template>
  <AnalyticsWidget
    title="Production Panels Breakdown"
    subTitle="Production panels started/completed/recut production during the date ranges"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight">

    <LoadingPanel :loading="loading" />
    <AnalyticsBreakdownTable v-if="!loading">
      <template v-slot:header>
        <tr>
          <th></th>
          <th>Started Count</th>
          <th>Started Area</th>
          <th>Completed Count</th>
          <th>Completed Area</th>
          <th>Recut Count</th>
          <th>Recut Area</th>
        </tr>
      </template>
      <template v-slot:body>
        <tr class="highlight">
          <td>
            Today
            <InlineHelpPopover title="Today" placement="right" inline>
              <p>Today&apos;s panels compared to previous working day&apos;s panels on {{formatDate(panelBreakdownData.day.previous.startDate)}}</p>
            </InlineHelpPopover>
          </td>
          <AnalyticsBreakdownValueCell :value="panelBreakdownData.day.current.startedTotalQuantity" :previous="panelBreakdownData.day.previous.startedTotalQuantity">{{formatInteger(panelBreakdownData.day.current.startedTotalQuantity, false, true)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="panelBreakdownData.day.current.startedTotalSquareMetre" :previous="panelBreakdownData.day.previous.startedTotalSquareMetre">{{formatDecimal(panelBreakdownData.day.current.startedTotalSquareMetre, 2, true)}}m&sup2;</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="panelBreakdownData.day.current.completedTotalQuantity" :previous="panelBreakdownData.day.previous.completedTotalQuantity">{{formatInteger(panelBreakdownData.day.current.completedTotalQuantity, false, true)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="panelBreakdownData.day.current.completedTotalSquareMetre" :previous="panelBreakdownData.day.previous.completedTotalSquareMetre">{{formatDecimal(panelBreakdownData.day.current.completedTotalSquareMetre, 2, true)}}m&sup2;</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="panelBreakdownData.day.current.recutTotalQuantity" :previous="panelBreakdownData.day.previous.recutTotalQuantity">{{formatInteger(panelBreakdownData.day.current.recutTotalQuantity, false, true)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="panelBreakdownData.day.current.recutTotalSquareMetre" :previous="panelBreakdownData.day.previous.recutTotalSquareMetre">{{formatDecimal(panelBreakdownData.day.current.recutTotalSquareMetre, 2, true)}}m&sup2;</AnalyticsBreakdownValueCell>
        </tr>
        <tr>
          <td>
            This Week
            <InlineHelpPopover title="This Week" placement="right" inline>
              <p>This week&apos;s panels compared to same day of the previous week&apos;s panels from {{formatDate(panelBreakdownData.week.previous.startDate)}} to {{formatDate(panelBreakdownData.week.previous.endDate)}}</p>
            </InlineHelpPopover>
          </td>
          <AnalyticsBreakdownValueCell :value="panelBreakdownData.week.current.startedTotalQuantity" :previous="panelBreakdownData.week.previous.startedTotalQuantity">{{formatInteger(panelBreakdownData.week.current.startedTotalQuantity, false, true)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="panelBreakdownData.week.current.startedTotalSquareMetre" :previous="panelBreakdownData.week.previous.startedTotalSquareMetre">{{formatDecimal(panelBreakdownData.week.current.startedTotalSquareMetre, 2, true)}}m&sup2;</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="panelBreakdownData.week.current.completedTotalQuantity" :previous="panelBreakdownData.week.previous.completedTotalQuantity">{{formatInteger(panelBreakdownData.week.current.completedTotalQuantity, false, true)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="panelBreakdownData.week.current.completedTotalSquareMetre" :previous="panelBreakdownData.week.previous.completedTotalSquareMetre">{{formatDecimal(panelBreakdownData.week.current.completedTotalSquareMetre, 2, true)}}m&sup2;</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="panelBreakdownData.week.current.recutTotalQuantity" :previous="panelBreakdownData.week.previous.recutTotalQuantity">{{formatInteger(panelBreakdownData.week.current.recutTotalQuantity, false, true)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="panelBreakdownData.week.current.recutTotalSquareMetre" :previous="panelBreakdownData.week.previous.recutTotalSquareMetre">{{formatDecimal(panelBreakdownData.week.current.recutTotalSquareMetre, 2, true)}}m&sup2;</AnalyticsBreakdownValueCell>
        </tr>
        <tr>
          <td>
            This Month
            <InlineHelpPopover title="This Week" placement="right" inline>
              <p>This month&apos;s panels compared to same day of the previous month&apos;s panels from {{formatDate(panelBreakdownData.month.previous.startDate)}} to {{formatDate(panelBreakdownData.month.previous.endDate)}}</p>
            </InlineHelpPopover>
          </td>
          <AnalyticsBreakdownValueCell :value="panelBreakdownData.month.current.startedTotalQuantity" :previous="panelBreakdownData.month.previous.startedTotalQuantity">{{formatInteger(panelBreakdownData.month.current.startedTotalQuantity, false, true)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="panelBreakdownData.month.current.startedTotalSquareMetre" :previous="panelBreakdownData.month.previous.startedTotalSquareMetre">{{formatDecimal(panelBreakdownData.month.current.startedTotalSquareMetre, 2, true)}}m&sup2;</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="panelBreakdownData.month.current.completedTotalQuantity" :previous="panelBreakdownData.month.previous.completedTotalQuantity">{{formatInteger(panelBreakdownData.month.current.completedTotalQuantity, false, true)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="panelBreakdownData.month.current.completedTotalSquareMetre" :previous="panelBreakdownData.month.previous.completedTotalSquareMetre">{{formatDecimal(panelBreakdownData.month.current.completedTotalSquareMetre, 2, true)}}m&sup2;</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="panelBreakdownData.month.current.recutTotalQuantity" :previous="panelBreakdownData.month.previous.recutTotalQuantity">{{formatInteger(panelBreakdownData.month.current.recutTotalQuantity, false, true)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="panelBreakdownData.month.current.recutTotalSquareMetre" :previous="panelBreakdownData.month.previous.recutTotalSquareMetre">{{formatDecimal(panelBreakdownData.month.current.recutTotalSquareMetre, 2, true)}}m&sup2;</AnalyticsBreakdownValueCell>
        </tr>
        <tr>
          <td>
            This FY
            <InlineHelpPopover title="This Week" placement="right" inline>
              <p>This financial year&apos;s panels compared to same day of the previous financial year&apos;s panels from {{formatDate(panelBreakdownData.financialYear.previous.startDate)}} to {{formatDate(panelBreakdownData.financialYear.previous.endDate)}}</p>
            </InlineHelpPopover>
          </td>
          <AnalyticsBreakdownValueCell :value="panelBreakdownData.financialYear.current.startedTotalQuantity" :previous="panelBreakdownData.financialYear.previous.startedTotalQuantity">{{formatInteger(panelBreakdownData.financialYear.current.startedTotalQuantity, false, true)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="panelBreakdownData.financialYear.current.startedTotalSquareMetre" :previous="panelBreakdownData.financialYear.previous.startedTotalSquareMetre">{{formatDecimal(panelBreakdownData.financialYear.current.startedTotalSquareMetre, 2, true)}}m&sup2;</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="panelBreakdownData.financialYear.current.completedTotalQuantity" :previous="panelBreakdownData.financialYear.previous.completedTotalQuantity">{{formatInteger(panelBreakdownData.financialYear.current.completedTotalQuantity, false, true)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="panelBreakdownData.financialYear.current.completedTotalSquareMetre" :previous="panelBreakdownData.financialYear.previous.completedTotalSquareMetre">{{formatDecimal(panelBreakdownData.financialYear.current.completedTotalSquareMetre, 2, true)}}m&sup2;</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="panelBreakdownData.financialYear.current.recutTotalQuantity" :previous="panelBreakdownData.financialYear.previous.recutTotalQuantity">{{formatInteger(panelBreakdownData.financialYear.current.recutTotalQuantity, false, true)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="panelBreakdownData.financialYear.current.recutTotalSquareMetre" :previous="panelBreakdownData.financialYear.previous.recutTotalSquareMetre">{{formatDecimal(panelBreakdownData.financialYear.current.recutTotalSquareMetre, 2, true)}}m&sup2;</AnalyticsBreakdownValueCell>          
        </tr>
      </template>

    </AnalyticsBreakdownTable>
  </AnalyticsWidget>
</template>


<script>
import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsBreakdownTable from '@/modules/intelligence/components/analyticsBreakdownTable'
import AnalyticsBreakdownValueCell from '@/modules/intelligence/components/analyticsBreakdownValueCell'

export default {
  name: 'ProductionPanelBreakdownWidget',
  components: {
    AnalyticsWidget,
    AnalyticsBreakdownTable,
    AnalyticsBreakdownValueCell
  },
  props: {
    gridWidth: Number,
    gridHeight: Number
  },
  computed: {
  },
  data() {
    return {
      loading: true,
      panelBreakdownData: {}
    }
  },
  watch: {
  },
  mounted() {
    this.loading = true
    this.requestData()

    this.subscribeEndpoint('/production/panelschedule', () => {
      this.requestData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/production/panelschedule')
  },
  methods: {
    requestData() {
      this.$apiService.analyticsService.getProductionBreakdownSummary()
      .then(response => {
        this.panelBreakdownData = response.data
      })
      .finally(() => {
        this.loading = false
      })
      .catch(err => {
      })
    },
    
  }
}
</script>